import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { Select } from '../components/Select.tsx';

interface ImportResult {
  imported: number;
  updated: number;
  skipped: number;
  imagesRewritten: number;
  imageFailures: number;
  imageFailureDetails: Array<{ articleTitle: string; src: string; reason: string }>;
  categoriesCreated: number;
  articlesScanned: number;
  failures: Array<{ title: string; error: string }>;
}

interface ZendeskStatus {
  configured: boolean;
  subdomain: string | null;
}

export function KbImportZendeskPage(): React.ReactElement {
  const { agent } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ZendeskStatus | null>(null);
  const [audience, setAudience] = useState<'public' | 'internal'>('public');
  const [publishImmediately, setPublishImmediately] = useState(true);
  const [reimportImages, setReimportImages] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    apiFetch<ZendeskStatus>('/api/admin/kb-import/zendesk/status')
      .then(setStatus)
      .catch(() => setStatus({ configured: false, subdomain: null }));
  }, []);

  if (!agent || agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await apiFetch<ImportResult>('/api/admin/kb-import/zendesk', {
        method: 'POST',
        json: { audience, publishImmediately, reimportImages },
      });
      setResult(res);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Import failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <Link to="/kb" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Knowledgebase
        </Link>
        <h1 className="mt-1 text-2xl font-bold text-[var(--color-header)]">Import from Zendesk</h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Pulls all published Help Center articles from your Zendesk subdomain and creates them as
          T3 KB articles. Categories are recreated to match. Articles whose slugs already exist are
          skipped unless you tick the re-import box below.
        </p>
      </div>

      {status && !status.configured && (
        <Card className="border-[var(--color-danger)]/40 bg-red-50/40">
          <h2 className="text-sm font-semibold text-[var(--color-danger)]">
            Zendesk credentials not configured
          </h2>
          <p className="mt-1 text-sm text-[var(--color-body)]">
            Set <code>ZENDESK_SUBDOMAIN</code>, <code>ZENDESK_EMAIL</code>, and{' '}
            <code>ZENDESK_API_TOKEN</code> in the server environment, then redeploy. The import uses
            those — there's nothing to enter here.
          </p>
        </Card>
      )}

      {status?.configured && (
        <Card>
          <form onSubmit={submit} className="space-y-4">
            <div className="rounded-[var(--radius-button)] bg-[var(--color-surface)] px-3 py-2 text-xs text-[var(--color-muted)]">
              Connecting to <code className="text-[var(--color-header)]">{status.subdomain}.zendesk.com</code>{' '}
              using server-configured credentials.
            </div>
            <Select
              label="Default audience"
              value={audience}
              onChange={(e) => setAudience(e.target.value as 'public' | 'internal')}
            >
              <option value="public">Public — clients can read in portal</option>
              <option value="internal">Internal — agents only</option>
            </Select>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={publishImmediately}
                onChange={(e) => setPublishImmediately(e.target.checked)}
                className="h-4 w-4 accent-[var(--color-t3-blue)]"
              />
              Publish imported articles immediately (uncheck to import as drafts)
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={reimportImages}
                onChange={(e) => setReimportImages(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[var(--color-t3-blue)]"
              />
              <span>
                <span className="font-medium text-[var(--color-header)]">
                  Re-import existing articles to fix images
                </span>
                <span className="block text-xs text-[var(--color-muted)]">
                  Off by default articles with the same slug are skipped. Tick this to re-fetch
                  the body from Zendesk, download images locally, and overwrite the existing T3
                  article body — category, audience, and publish state are preserved.
                </span>
              </span>
            </label>
            {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
            <Button type="submit" disabled={busy}>
              {busy ? 'Importing…' : 'Import'}
            </Button>
          </form>
        </Card>
      )}

      {result && (
        <Card className="border-green-300 bg-green-50/40">
          <h2 className="text-base font-semibold text-green-900">Import complete</h2>
          <ul className="mt-2 space-y-1 text-sm text-green-900">
            <li>Articles scanned: {result.articlesScanned}</li>
            <li>Imported (new): <strong>{result.imported}</strong></li>
            <li>Updated (re-imported): <strong>{result.updated}</strong></li>
            <li>Skipped (drafts or already exist): {result.skipped}</li>
            <li>
              Images downloaded + rewritten: <strong>{result.imagesRewritten}</strong>
              {result.imageFailures > 0 && (
                <span className="ml-2 text-[var(--color-danger)]">
                  · {result.imageFailures} image fetch failures
                </span>
              )}
            </li>
            <li>Categories created: {result.categoriesCreated}</li>
          </ul>
          {result.failures.length > 0 && (
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer text-[var(--color-danger)]">
                {result.failures.length} article failures (click to expand)
              </summary>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {result.failures.map((f, i) => (
                  <li key={i}>
                    <strong>{f.title}</strong>: {f.error}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {result.imageFailureDetails.length > 0 && (
            <details className="mt-3 text-sm" open>
              <summary className="cursor-pointer text-[var(--color-danger)]">
                {result.imageFailureDetails.length} image failures (click to expand) — these
                images stayed as their original URL and will render broken. Common reasons:
                Zendesk hotlink protection (403), CDN auth required, or article references an
                offline asset.
              </summary>
              <ul className="mt-2 max-h-72 list-disc space-y-1 overflow-y-auto pl-5 font-mono text-xs">
                {result.imageFailureDetails.map((f, i) => (
                  <li key={i}>
                    <strong>{f.articleTitle}</strong>
                    <div className="break-all text-[var(--color-muted)]">{f.src}</div>
                    <div className="text-[var(--color-danger)]">{f.reason}</div>
                  </li>
                ))}
              </ul>
            </details>
          )}
          <div className="mt-3">
            <Button variant="outline" onClick={() => navigate('/kb')}>
              View KB
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
